import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import { PlatformSubSection } from "@tracabois/template"
import { Action } from "@komune-io/g2-components"
import { Stack, Typography } from "@mui/material"
import { usePage } from "@tracabois/utils"
import { usePopup } from "@tracabois/components"
import { OrganizationRef } from "../../../../../referentiels/organizationType"
import {
  Realization,
  RealizationAssignActorCommand,
  RealizationAssignedMoeEvent,
  RealizationAssignMoeCommand,
  RealizationRemovedMoeEvent,
  RealizationRemoveMoeCommand,
  RealizationRemoveActorCommand,
} from "../../../model/realizationModel"
import { useRealizationAssignActorMutation, useRealizationAssignMoeMutation, useRealizationRemoveMoeMutation, useRealizationRemoveActorMutation } from "../../../api"
import { RealizationActorsTable } from "./RealizationActorsTable"
import { RealizationMoeAddFormPopup } from "../../form/moeCreateForm/RealizationMoeAddFormPopup"
import { RealizationActorAddFormPopup } from "../../form/actorCreateForm/RealizationActorAddFormPopup"
import { RealizationMoeTable } from "../../table/RealizationMoeTable"

interface RealizationActorsSectionProps {
  realization?: Realization
  onMoeAssigned?: (event: RealizationAssignedMoeEvent) => void
  onMoeRemoved?: (event: RealizationRemovedMoeEvent) => void
  onActorAssigned?: () => void
  onActorRemoved?: () => void
  isLoading?: boolean
  editable?: boolean
}

const useLabels = () => {
  const { t } = useTranslation()
  return useMemo(() => ({
    projectActors: t("realization.actors.project", "Acteurs du projet"),
    transformationActors: t("realization.actors.transformation", "Acteurs de la transformation"),
    directActors: t("realization.actors.direct", "Acteurs directs"),
    ouvrageActors: t("realization.actors.ouvrages", "Acteurs provenant des ouvrages"),
    addMoe: t("realization.moe.add", "Ajouter un acteur"),
    addActor: t("realization.actor.add", "Ajouter un acteur"),
  }), [t])
}

export const RealizationActorsSection = (props: RealizationActorsSectionProps) => {
  const { realization, onMoeAssigned, onMoeRemoved, onActorAssigned, onActorRemoved, isLoading, editable } = props
  const labels = useLabels()
  const navigate = useNavigate()
  const moePopup = usePopup()
  const actorPopup = usePopup()
  const [assignMoeMutation] = useRealizationAssignMoeMutation()
  const [removeMoeMutation] = useRealizationRemoveMoeMutation()
  const [assignActorMutation] = useRealizationAssignActorMutation()
  const [removeActorMutation] = useRealizationRemoveActorMutation()

  // Project actors (moes)
  const moesPage = usePage<OrganizationRef>(realization?.moes)

  // Split actors: ouvrage actors have products, direct (CSV-imported) actors don't
  const { ouvrageActors, directActors } = useMemo(() => {
    const ouvrageMap = new Map<string, OrganizationRef>()
    const directMap = new Map<string, OrganizationRef>()
    realization?.actors?.forEach((actor) => {
      if (actor.organizationRef?.id) {
        const target = actor.product && actor.product.length > 0 ? ouvrageMap : directMap
        target.set(actor.organizationRef.id, actor.organizationRef)
      }
    })
    return {
      ouvrageActors: Array.from(ouvrageMap.values()),
      directActors: Array.from(directMap.values()),
    }
  }, [realization?.actors])
  const ouvrageActorsPage = usePage<OrganizationRef>(ouvrageActors)
  const directActorsPage = usePage<OrganizationRef>(directActors)

  const moeActions: Action[] = [{
    key: "addMoe",
    label: labels.addMoe,
    showIf: () => editable || false,
    onClick: moePopup.open,
  }]

  const actorActions: Action[] = [{
    key: "addActor",
    label: labels.addActor,
    showIf: () => editable || false,
    onClick: actorPopup.open,
  }]

  const assignMoe = useCallback(async (command: RealizationAssignMoeCommand) => {
    const event = await assignMoeMutation(command)
    // @ts-ignore
    onMoeAssigned?.(event.data)
    moePopup.close()
    return true
  }, [assignMoeMutation, moePopup, onMoeAssigned])

  const removeMoe = useCallback(async (command: RealizationRemoveMoeCommand) => {
    const event = await removeMoeMutation(command)
    // @ts-ignore
    onMoeRemoved?.(event.data)
    return true
  }, [removeMoeMutation, onMoeRemoved])

  const assignActor = useCallback(async (command: RealizationAssignActorCommand) => {
    await assignActorMutation(command)
    onActorAssigned?.()
    actorPopup.close()
    return true
  }, [assignActorMutation, actorPopup, onActorAssigned])

  const removeActor = useCallback(async (command: RealizationRemoveActorCommand) => {
    await removeActorMutation(command)
    onActorRemoved?.()
    return true
  }, [removeActorMutation, onActorRemoved])

  const handleRowClicked = useCallback((row: { original: OrganizationRef }) => {
    if (row.original.id) {
      navigate(`/organizations/${row.original.id}/edit`)
    }
  }, [navigate])

  return (
    <>
      <PlatformSubSection title={labels.projectActors} actions={moeActions}>
        {realization?.id && (
          <RealizationMoeTable
            page={moesPage.page}
            realizationId={realization.id}
            isLoading={isLoading}
            editable={editable}
            onPageQueryChange={moesPage.setPageQuery}
            onRowClicked={handleRowClicked}
            onRemoveMoe={removeMoe}
          />
        )}

        {editable && realization?.id && moePopup.isOpen && (
          <RealizationMoeAddFormPopup
            realizationId={realization.id}
            onClose={moePopup.close}
            open={moePopup.isOpen}
            onSubmit={assignMoe}
          />
        )}
      </PlatformSubSection>

      <PlatformSubSection title={labels.transformationActors} actions={actorActions}>
        <Stack spacing={3}>
          {directActors.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary">
                {labels.directActors}
              </Typography>
              <RealizationActorsTable
                page={directActorsPage.page}
                isLoading={isLoading}
                editable={editable}
                realizationId={realization?.id}
                onPageQueryChange={directActorsPage.setPageQuery}
                onRowClicked={handleRowClicked}
                onRemoveActor={removeActor}
              />
            </Stack>
          )}

          {ouvrageActors.length > 0 && (
            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary">
                {labels.ouvrageActors}
              </Typography>
              <RealizationActorsTable
                page={ouvrageActorsPage.page}
                isLoading={isLoading}
                onPageQueryChange={ouvrageActorsPage.setPageQuery}
                onRowClicked={handleRowClicked}
              />
            </Stack>
          )}
        </Stack>

        {editable && realization?.id && actorPopup.isOpen && (
          <RealizationActorAddFormPopup
            realizationId={realization.id}
            onClose={actorPopup.close}
            open={actorPopup.isOpen}
            onSubmit={assignActor}
          />
        )}
      </PlatformSubSection>
    </>
  )
}
